import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import IStore from "models/IStore";
import RoleEnum from "constants/RoleEnum";
import RouteEnum from "constants/RouteEnum";
import IUserCookieModel from "models/common-models/IUserCookieModel";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface IProps {
  readonly showtitle: boolean;
  readonly sideBarStatus: boolean;
}

const UserMenu: React.FC<IProps> = (props: React.PropsWithChildren<IProps>) => {
  const { t } = useTranslation();
  const { showtitle, sideBarStatus } = props;

  const selectedUserProfile = useSelector((state: IStore) => state.auth.selectedProfile);

  const user: IUserCookieModel | null = useSelector((state: IStore) => state.auth.user);

  const renderTooltip = (message: string) => <Tooltip id="sidebar-tooltip">{message}</Tooltip>;

  const overlayTrigger = (message: string, icoFontClassName: string) => {
    return (
      <OverlayTrigger
        placement="right"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip id="sidebar-tooltip">{message}</Tooltip>}
      >
        <i className={icoFontClassName} />
      </OverlayTrigger>
    );
  };

  return (
    <Fragment>
      {user && (
        <Fragment>
          <li>
            <NavLink to={RouteEnum.Profile} className="menu-link">
              {sideBarStatus ? (
                overlayTrigger(t("general.my-profile"), "icofont icofont-user")
              ) : (
                <i className="icofont icofont-user" />
              )}
              {showtitle && <span className="menu-title">{t("general.my-profile")}</span>}
            </NavLink>
          </li>
          {
            //Todo must be components
            selectedUserProfile === RoleEnum.Actor && user.actor_slug && (
              <Fragment>
                <li>
                  <NavLink
                    to={RouteEnum.ActorProfile.replace(":uuid", user.actor_uuid)}
                    className="menu-link"
                  >
                    {sideBarStatus ? (
                      overlayTrigger(user.full_name, "icofont icofont-royal")
                    ) : (
                      <i className="icofont icofont-royal"></i>
                    )}
                    {showtitle && <span className="menu-title">{user.full_name}</span>}
                  </NavLink>
                </li>
                <li>
                  <NavLink to={RouteEnum.RoleInvitations} className="menu-link">
                    {sideBarStatus ? (
                      overlayTrigger(t("leftSideMenu.myInvitations"), "icofont icofont-letterbox")
                    ) : (
                      <i className="icofont icofont-letterbox" />
                    )}

                    {showtitle && <span className="menu-title">{t("leftSideMenu.myInvitations")}</span>}
                  </NavLink>
                </li>
                <li>
                  <NavLink to={RouteEnum.RoleApplications} className="menu-link">
                    {sideBarStatus ? (
                      <OverlayTrigger
                        placement="right"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderTooltip(t("leftSideMenu.myApplication"))}
                      >
                        <i className="icofont icofont-page"></i>
                      </OverlayTrigger>
                    ) : (
                      <i className="icofont icofont-page"></i>
                    )}
                    {showtitle && <span className="menu-title">{t("leftSideMenu.myApplication")}</span>}
                  </NavLink>
                </li>
              </Fragment>
            )
          }

          {selectedUserProfile === RoleEnum.ProductionCompany && user.companies.production_company && (
            <Fragment>
              <li>
                <NavLink
                  to={RouteEnum.ProductionProfile.replace(":slug", user.companies.production_company.slug)}
                  className="menu-link"
                >
                  {sideBarStatus ? (
                    overlayTrigger(user.companies.production_company.name, "icofont icofont-film mr-2")
                  ) : (
                    <i className="icofont icofont-film mr-2" />
                  )}
                  {showtitle && <span className="menu-title">{user.companies.production_company.name}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to={RouteEnum.ProductionPayments} className="menu-link">
                  {sideBarStatus ? (
                    overlayTrigger(t(`leftSideMenu.payments`), "icofont icofont-bill mr-2")
                  ) : (
                    <i className="icofont icofont-bill mr-2" />
                  )}
                  {showtitle && <span className="menu-title">{t(`leftSideMenu.payments`)}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to={RouteEnum.Reports} className="menu-link">
                  {sideBarStatus ? (
                    overlayTrigger(t(`leftSideMenu.reports`), "icofont icofont-dashboard-web mr-2")
                  ) : (
                    <i className="icofont icofont-dashboard-web mr-2"></i>
                  )}
                  {showtitle && <span className="menu-title">{t(`leftSideMenu.reports`)}</span>}
                </NavLink>
              </li>
            </Fragment>
          )}

          {selectedUserProfile === RoleEnum.CastAgency && user.companies.cast_agency && (
            <Fragment>
              <li>
                <NavLink
                  to={RouteEnum.CastProfile.replace(":slug", user.companies.cast_agency.slug)}
                  className="menu-link"
                >
                  {sideBarStatus ? (
                    overlayTrigger(user.companies.cast_agency.name, "icofont icofont-people mr-2")
                  ) : (
                    <i className="icofont icofont-people mr-2" />
                  )}
                  {showtitle && <span className="menu-title">{user.companies.cast_agency.name}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to={RouteEnum.ProjectCastProjects} className="menu-link">
                  {sideBarStatus ? (
                    overlayTrigger(
                      t(`leftSideMenu.agencyProfile.recomendedActorProjects`),
                      "icofont icofont-letterbox mr-2",
                    )
                  ) : (
                    <i className="icofont icofont-letterbox mr-2" />
                  )}
                  {showtitle && (
                    <span className="menu-title">{t(`leftSideMenu.agencyProfile.recomendedActorProjects`)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to={RouteEnum.CastPayments} className="menu-link">
                  {sideBarStatus ? (
                    overlayTrigger(t(`leftSideMenu.payments`), "icofont icofont-bill mr-2")
                  ) : (
                    <i className="icofont icofont-bill mr-2" />
                  )}
                  {showtitle && <span className="menu-title">{t(`leftSideMenu.payments`)}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to={RouteEnum.Reports} className="menu-link">
                  {sideBarStatus ? (
                    overlayTrigger(t(`leftSideMenu.reports`), "icofont icofont-dashboard-web mr-2")
                  ) : (
                    <i className="icofont icofont-dashboard-web mr-2"></i>
                  )}
                  {showtitle && <span className="menu-title">{t(`leftSideMenu.reports`)}</span>}
                </NavLink>
              </li>
            </Fragment>
          )}

          {selectedUserProfile === RoleEnum.Client && user.companies.client && (
            <li>
              <NavLink
                to={RouteEnum.ClientProfile.replace(":slug", user.companies.client.slug)}
                className="menu-link"
              >
                {sideBarStatus ? (
                  overlayTrigger(user.companies.client.name, "icofont icofont-live-messenger mr-2")
                ) : (
                  <i className="icofont icofont-live-messenger mr-2" />
                )}
                {showtitle && <span className="menu-title">{user.companies.client.name}</span>}
              </NavLink>
            </li>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default UserMenu;
